import type { Editor } from '@tiptap/core';
import type { HeaderFooter } from '@/editor/headerFooter';
import { store } from '@/state/store';
import { DEFAULT_PAGE_SETUP, DEFAULT_PAGE_NUMBER } from '@/state/types';
import { importDocx } from '@/docx/import';
import { exportDocx } from '@/docx/export';
import {
  readFile, writeFile, saveFileAs, pickDocxFile,
} from './fileSystem';
import {
  pickDriveDocx, downloadDriveFile, updateDriveFile, createDriveFile,
} from './googleDrive';
import { saveDraft, clearDraft } from './db';
import { cleanOrphanMarks } from '@/editor/commentService';
import { LOAD_CONTENT_META } from '@/editor/trackChanges';

// ═══════════════════════════════════════════════════════════════
// Document service — orchestruje nový/otevřít/uložit/uložit jako
// a automatické ukládání na pozadí (sekce 2.4, 2.4b).
// ═══════════════════════════════════════════════════════════════

let editorRef: Editor | null = null;
let headerFooterRef: HeaderFooter | null = null;
let autosaveTimer: number | null = null;

export function bindEditor(editor: Editor): void {
  editorRef = editor;
}

export function bindHeaderFooter(hf: HeaderFooter): void {
  headerFooterRef = hf;
}

/**
 * Nahraje obsah do editoru s příznakem, že jde o načtení dokumentu — ne
 * o uživatelskou úpravu. Bez toho by sledování změn (když je zapnuté)
 * označilo celý otevřený dokument jako vložený text.
 */
function loadContent(html: string): void {
  if (!editorRef) return;
  editorRef
    .chain()
    .command(({ tr }) => {
      tr.setMeta(LOAD_CONTENT_META, true);
      return true;
    })
    .setContent(html)
    .run();
}

/** Sestaví argumenty exportu z aktuálního stavu (tělo + záhlaví/zápatí). */
function exportArgs() {
  // Před exportem ukliď marky bez komentáře — osiřelá reference by udělala
  // .docx, který Word odmítne otevřít jako poškozený.
  if (editorRef) cleanOrphanMarks(editorRef);
  const state = store.get();
  return {
    body: editorRef!.getHTML(),
    page: state.doc.pageSetup,
    header: state.doc.headerHtml || '',
    footer: state.doc.footerHtml || '',
    pageNumber: state.doc.pageNumber || DEFAULT_PAGE_NUMBER,
    comments: state.doc.comments || [],
  };
}

/** Nový prázdný dokument (bez vazby na soubor). */
export async function newDocument(): Promise<void> {
  if (!editorRef) return;
  loadContent('<p></p>');
  headerFooterRef?.setContent('<p></p>', '<p></p>');
  store.patchDoc({
    name: 'Nepojmenovaný dokument',
    source: 'unsaved',
    fileHandle: undefined,
    gdriveId: undefined,
    dirty: false,
    lastSavedAt: undefined,
    pageSetup: { ...DEFAULT_PAGE_SETUP, margins: { ...DEFAULT_PAGE_SETUP.margins } },
    headerHtml: '',
    footerHtml: '',
    pageNumber: { ...DEFAULT_PAGE_NUMBER },
    comments: [],
  });
  await clearDraft();
}

/** Otevře .docx z předaného handle (z file exploreru nebo pickeru). */
export async function openFromHandle(handle: FileSystemFileHandle): Promise<void> {
  if (!editorRef) return;
  const buf = await readFile(handle);
  const { html, name, headerHtml, footerHtml, comments } = await importDocx(buf, handle.name);
  loadContent(html);
  headerFooterRef?.setContent(headerHtml || '<p></p>', footerHtml || '<p></p>');
  store.patchDoc({
    name,
    source: 'local',
    fileHandle: handle,
    gdriveId: undefined,
    dirty: false,
    lastSavedAt: Date.now(),
    headerHtml: headerHtml || '',
    footerHtml: footerHtml || '',
    comments: comments || [],
  });
  await clearDraft();
}

/** Otevře přes systémový file picker. */
export async function openViaPicker(): Promise<void> {
  const handle = await pickDocxFile();
  if (handle) await openFromHandle(handle);
}

/** Otevře .docx z Google Disku (Picker → stažení → import). */
export async function openFromDrive(): Promise<void> {
  if (!editorRef) return;
  const pick = await pickDriveDocx();
  if (!pick) return; // uživatel zrušil
  store.set({ saveStatus: 'saving' });
  try {
    const buf = await downloadDriveFile(pick.id);
    const { html, name, headerHtml, footerHtml, comments } = await importDocx(buf, pick.name);
    loadContent(html);
    headerFooterRef?.setContent(headerHtml || '<p></p>', footerHtml || '<p></p>');
    store.patchDoc({
      name: name || pick.name.replace(/\.docx$/i, ''),
      source: 'gdrive',
      gdriveId: pick.id,
      fileHandle: undefined,
      dirty: false,
      lastSavedAt: Date.now(),
      headerHtml: headerHtml || '',
      footerHtml: footerHtml || '',
      comments: comments || [],
    });
    store.set({ saveStatus: 'saved' });
    await clearDraft();
  } catch (err) {
    console.error('Otevření z Google Disku selhalo:', err);
    store.set({ saveStatus: 'error' });
    alert('Otevření z Google Disku selhalo:\n' + (err instanceof Error ? err.message : String(err)));
  }
}

/** Uloží aktuální dokument na Google Disk jako nový soubor. */
export async function saveToDriveAs(): Promise<void> {
  const state = store.get();
  if (!editorRef) return;
  store.set({ saveStatus: 'saving' });
  try {
    const blob = await exportDocx(exportArgs());
    const id = await createDriveFile(state.doc.name || 'dokument', blob);
    store.patchDoc({
      source: 'gdrive',
      gdriveId: id,
      fileHandle: undefined,
      dirty: false,
      lastSavedAt: Date.now(),
    });
    store.set({ saveStatus: 'saved' });
    await clearDraft();
  } catch (err) {
    console.error('Uložení na Google Disk selhalo:', err);
    store.set({ saveStatus: 'error' });
  }
}

/** Uloží do aktivního souboru (Ctrl+S). Když není vazba, spadne do Uložit jako. */
export async function save(): Promise<void> {
  const state = store.get();
  if (!editorRef) return;

  if (state.doc.source === 'local' && state.doc.fileHandle) {
    store.set({ saveStatus: 'saving' });
    try {
      const blob = await exportDocx(exportArgs());
      await writeFile(state.doc.fileHandle, blob);
      store.patchDoc({ dirty: false, lastSavedAt: Date.now() });
      store.set({ saveStatus: 'saved' });
    } catch {
      store.set({ saveStatus: 'error' });
    }
    return;
  }

  // Dokument otevřený z Google Disku → aktualizuj tentýž soubor.
  if (state.doc.source === 'gdrive' && state.doc.gdriveId) {
    store.set({ saveStatus: 'saving' });
    try {
      const blob = await exportDocx(exportArgs());
      await updateDriveFile(state.doc.gdriveId, blob);
      store.patchDoc({ dirty: false, lastSavedAt: Date.now() });
      store.set({ saveStatus: 'saved' });
    } catch (err) {
      console.error('Uložení na Google Disk selhalo:', err);
      store.set({ saveStatus: 'error' });
    }
    return;
  }

  // Neuložený → Uložit jako (lokálně)
  await saveAs();
}

/** Uložit jako — nový soubor přes save picker. */
export async function saveAs(): Promise<void> {
  const state = store.get();
  if (!editorRef) return;
  store.set({ saveStatus: 'saving' });
  try {
    const blob = await exportDocx(exportArgs());
    const suggested = (state.doc.name || 'dokument') + '.docx';
    const handle = await saveFileAs(blob, suggested);
    if (!handle) {
      store.set({ saveStatus: 'idle' }); // uživatel zrušil
      return;
    }
    store.patchDoc({
      name: handle.name.replace(/\.docx$/i, ''),
      source: 'local',
      fileHandle: handle,
      dirty: false,
      lastSavedAt: Date.now(),
    });
    store.set({ saveStatus: 'saved' });
    await clearDraft();
  } catch {
    store.set({ saveStatus: 'error' });
  }
}

// ── Automatické ukládání na pozadí (~1× za minutu), sekce 2.4b ──
const AUTOSAVE_MS = 60_000;

export function startAutosave(): void {
  stopAutosave();
  autosaveTimer = window.setInterval(runAutosave, AUTOSAVE_MS);
}

export function stopAutosave(): void {
  if (autosaveTimer !== null) {
    clearInterval(autosaveTimer);
    autosaveTimer = null;
  }
}

async function runAutosave(): Promise<void> {
  const state = store.get();
  if (!editorRef || !state.doc.dirty) return;

  // Uložený lokální soubor → zapiš přímo do něj (do cílového souboru, ne do
  // odděleného concept úložiště).
  if (state.doc.source === 'local' && state.doc.fileHandle) {
    await save();
    return;
  }

  // Nový neuložený dokument → mezistav do IndexedDB (crash recovery).
  if (state.doc.source === 'unsaved') {
    try {
      await saveDraft(editorRef.getHTML(), state.doc.name);
      store.set({ saveStatus: 'saved' });
    } catch {
      /* tichá chyba autosave — neruší uživatele */
    }
  }
}
